/**
 * Theme files (the import/export interchange), the id rules, and the parsing
 * both sides of the library share: the strict file path, the lenient
 * stored-record path, the stored-record shape, and canonicalization.
 *
 * Split out of `themeLibrary.ts` (which owns the installed library, the
 * storage, and the halves) to keep both under the file-size ratchet.
 * Imports point one way: this module may use `themeColors` and the
 * `CustomThemePalette` type; `themeLibrary` uses this module's rules and
 * writers.
 *
 * @module
 */
import type { ThemeAppearance, ThemeColors } from "@cmmd-forge/contracts/theme";

import { DEFAULT_THEME_ID, THEME_PALETTES, THEME_ROLES } from "@cmmd-forge/contracts/theme";

import { toCanonicalThemeColor } from "./themeColors";
import type { CustomThemePalette } from "./themeLibrary";

const THEME_COLOR_ROLE_SET: ReadonlySet<string> = new Set(THEME_ROLES);

/** Ids no installed theme may occupy: the default, the built-ins, the appearance keywords. */
export const BUILT_IN_THEME_IDS: ReadonlySet<string> = new Set(THEME_PALETTES.map((p) => p.id));

export const RESERVED_THEME_IDS: ReadonlySet<string> = new Set([
  DEFAULT_THEME_ID,
  ...BUILT_IN_THEME_IDS,
  "light",
  "dark",
  "system",
]);

export const THEME_HALVES_STORAGE_KEY = "forge:theme-halves:v1";
/** Theme files carry a version so a future format can migrate rather than guess. */
export const THEME_FILE_VERSION = 1 as const;

type ThemeColorOverrides = Readonly<Record<string, string>>;

/** Upstream's shape: one appearance owns `colors`, the other rides in `variants`. */
type ThemeFileColors = Readonly<Record<string, string>>;

interface ThemeFile {
  readonly version: typeof THEME_FILE_VERSION;
  readonly id: string;
  readonly name: string;
  readonly appearance: ThemeAppearance;
  readonly colors: ThemeFileColors;
  readonly variants?: Readonly<Partial<Record<ThemeAppearance, ThemeFileColors>>>;
  readonly collection?: Readonly<{ id: string; label: string }>;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isThemeAppearance(value: unknown): value is ThemeAppearance {
  return value === "light" || value === "dark";
}

function isThemeId(value: unknown): value is string {
  return typeof value === "string" && /^[a-z0-9](?:[a-z0-9-]{0,47})$/.test(value);
}

function isThemeLabel(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.trim().length <= 48;
}

function parseThemeCollection(value: unknown): CustomThemePalette["collection"] {
  return isRecord(value) &&
    typeof value.id === "string" &&
    /^[a-z0-9][a-z0-9.:-]{0,127}$/i.test(value.id) &&
    isThemeLabel(value.label)
    ? { id: value.id, label: value.label.trim() }
    : undefined;
}

/**
 * Tolerates unknown roles and malformed values so themes written by other
 * builds (for example one with more roles) keep everything this build
 * understands. The lenient path: stored themes and environment records.
 */
function lenientThemeColorOverrides(value: Readonly<Record<string, unknown>>): ThemeColorOverrides {
  const overrides: Record<string, string> = {};
  for (const [role, color] of Object.entries(value)) {
    const normalized = toCanonicalThemeColor(color);
    if (THEME_COLOR_ROLE_SET.has(role) && normalized) {
      overrides[role] = normalized;
    }
  }
  return overrides;
}

/** The strict path: an imported file's colors must all be valid and known. */
function parseThemeColorOverrides(value: unknown): ThemeColorOverrides {
  if (!isRecord(value)) throw new Error("Theme colors must be objects.");

  const overrides: Record<string, string> = {};
  for (const [role, color] of Object.entries(value)) {
    if (!THEME_COLOR_ROLE_SET.has(role)) {
      throw new Error(`"${role}" is not a supported theme color role.`);
    }
    const normalized = toCanonicalThemeColor(color);
    if (!normalized) {
      throw new Error(
        `The color for "${role}" must be a literal CSS color such as oklch(0.62 0.2 280).`,
      );
    }
    overrides[role] = normalized;
  }
  if (Object.keys(overrides).length === 0) {
    throw new Error("Add at least one color role to the theme file.");
  }
  return overrides;
}

/** Ids no installed theme may occupy. */
export function isReservedThemeId(themeId: string): boolean {
  return RESERVED_THEME_IDS.has(themeId);
}

export function themeIdFromName(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return normalized || "custom-theme";
}

// ---------------------------------------------------------------------------
// Stored library: localStorage with a cached snapshot and change listeners.
// ---------------------------------------------------------------------------

function parseStoredHalfColors(value: unknown): ThemeColors | null {
  if (!isRecord(value)) return null;
  return lenientThemeColorOverrides(value) as ThemeColors;
}

function resolveStoredHalves(
  value: Record<string, unknown>,
  base: ThemeColors,
): { light: ThemeColors; dark: ThemeColors } | null {
  const variantValue = value.variants;
  let variant: ThemeColors;
  if (variantValue === undefined) {
    variant = {};
  } else {
    if (!isRecord(variantValue)) return null;
    const otherRaw = value.appearance === "light" ? variantValue.dark : variantValue.light;
    // A variant matching the base appearance would be shadowed by the base
    // colors; drop it so the theme round-trips through parseThemeFile.
    const parsed = parseStoredHalfColors(otherRaw ?? {});
    if (parsed === null) return null;
    variant = parsed;
  }
  return {
    light: value.appearance === "light" ? base : variant,
    dark: value.appearance === "dark" ? base : variant,
  };
}

/** The identity checks every stored theme record must pass, with the fields
 * the parser reads narrowed to their types. */
function isValidStoredThemeRecord(
  value: unknown,
): value is Record<string, unknown> & { id: string; label: string; appearance: ThemeAppearance } {
  return (
    isRecord(value) &&
    isThemeId(value.id) &&
    !RESERVED_THEME_IDS.has(value.id) &&
    isThemeLabel(value.label) &&
    isThemeAppearance(value.appearance)
  );
}

function parseStoredTheme(value: unknown): CustomThemePalette | null {
  if (!isValidStoredThemeRecord(value)) return null;
  const base = parseStoredHalfColors(value.colors);
  if (base === null) return null;
  const halves = resolveStoredHalves(value, base);
  if (halves === null) return null;
  const { light, dark } = halves;
  const collection = parseThemeCollection(value.collection);

  return {
    id: value.id,
    name: value.label.trim(),
    description: typeof value.description === "string" ? value.description : "",
    appearance: value.appearance,
    light,
    dark,
    ...(collection ? { collection } : {}),
  };
}

export function parseStoredThemes(
  storedThemes: ReadonlyArray<unknown>,
): ReadonlyArray<CustomThemePalette> {
  const themes: CustomThemePalette[] = [];
  for (const value of storedThemes) {
    const theme = parseStoredTheme(value);
    if (theme && !themes.some((existing) => existing.id === theme.id)) {
      themes.push(theme);
    }
  }
  return themes;
}

function canonicalizeHalf(colors: ThemeColors): ThemeColors {
  const canonical: Record<string, string> = {};
  for (const [role, color] of Object.entries(colors)) {
    const normalized = toCanonicalThemeColor(color);
    if (normalized) canonical[role] = normalized;
  }
  return canonical;
}

export function canonicalizeCustomTheme(theme: CustomThemePalette): CustomThemePalette {
  return {
    ...theme,
    light: canonicalizeHalf(theme.light),
    dark: canonicalizeHalf(theme.dark),
  };
}

export function toStoredRecord(theme: CustomThemePalette): Record<string, unknown> {
  const base = theme.appearance === "light" ? theme.light : theme.dark;
  const other = theme.appearance === "light" ? theme.dark : theme.light;
  const record: Record<string, unknown> = {
    version: THEME_FILE_VERSION,
    id: theme.id,
    label: theme.name,
    appearance: theme.appearance,
    colors: base,
  };
  // The lenient read restores a stored description; writing it here is what
  // makes it survive the reload in between.
  if (theme.description !== "") record.description = theme.description;
  if (Object.keys(other).length > 0) {
    record.variants = theme.appearance === "light" ? { dark: other } : { light: other };
  }
  if (theme.collection) record.collection = theme.collection;
  return record;
}

// ---------------------------------------------------------------------------
// Theme files: the import/export interchange.
// ---------------------------------------------------------------------------

// Theme files: the import/export interchange.
// ---------------------------------------------------------------------------

/**
 * Parse an imported theme file. Throws a descriptive error for every shape
 * this build cannot honor; colors are strict here (unknown roles and invalid
 * values are file errors, unlike the lenient stored path).
 *
 * Omitted roles stay omitted: forge palettes are partial, and a role the file
 * does not set keeps the shipped token. The other appearance comes from
 * `variants` and defaults to empty (fully shipped) rather than to a copy.
 */
function parseThemeFileHeader(value: Record<string, unknown>): {
  id: string;
  name: string;
  appearance: ThemeAppearance;
  collection: CustomThemePalette["collection"];
} {
  if (value.version !== THEME_FILE_VERSION) {
    throw new Error(`This theme file uses an unsupported version. Expected ${THEME_FILE_VERSION}.`);
  }

  const name = value.name;
  const appearance = value.appearance;
  if (!isThemeLabel(name)) throw new Error("Theme files need a name (48 characters or fewer).");
  if (!isThemeAppearance(appearance)) {
    throw new Error('Theme files need an appearance of "light" or "dark".');
  }

  const id = value.id === undefined ? themeIdFromName(name) : value.id;
  if (!isThemeId(id)) {
    throw new Error("Theme ids may only contain lowercase letters, numbers, and hyphens.");
  }
  if (RESERVED_THEME_IDS.has(id)) {
    throw new Error(`The theme id "${id}" is reserved.`);
  }

  const collection = parseThemeCollection(value.collection);
  if (value.collection !== undefined && !collection) {
    throw new Error("Theme collections need a valid id and label.");
  }

  return { id, name: name.trim(), appearance, collection };
}

function parseThemeFileVariants(
  value: Record<string, unknown>,
  appearance: ThemeAppearance,
): ThemeFileColors {
  if (value.variants === undefined) return {};

  if (!isRecord(value.variants)) throw new Error("Theme variants must be an object.");
  for (const [variantAppearance, variantColors] of Object.entries(value.variants)) {
    if (!isThemeAppearance(variantAppearance)) {
      throw new Error('Theme variants may only be named "light" or "dark".');
    }
    if (variantAppearance === appearance) {
      throw new Error(`Theme variants must not repeat the base appearance "${appearance}".`);
    }
    if (!isRecord(variantColors)) throw new Error("Theme variants must be objects.");
    return parseThemeColorOverrides(variantColors);
  }
  return {};
}

export function parseThemeFile(value: unknown): CustomThemePalette {
  if (!isRecord(value)) {
    throw new Error("Theme files must contain a JSON object.");
  }

  const { id, name, appearance, collection } = parseThemeFileHeader(value);
  if (!isRecord(value.colors)) throw new Error("Theme files need a colors object.");
  const base = parseThemeColorOverrides(value.colors);
  const other = parseThemeFileVariants(value, appearance);

  return {
    id,
    name,
    description: typeof value.description === "string" ? value.description : "",
    appearance,
    light: appearance === "light" ? base : other,
    dark: appearance === "dark" ? base : other,
    ...(collection ? { collection } : {}),
  };
}

/** Serialize a theme back into the importable file shape. */
export function serializeThemeFile(theme: CustomThemePalette): string {
  const canonicalTheme = canonicalizeCustomTheme(theme);
  const base = canonicalTheme.appearance === "light" ? canonicalTheme.light : canonicalTheme.dark;
  const other = canonicalTheme.appearance === "light" ? canonicalTheme.dark : canonicalTheme.light;
  const file: ThemeFile = {
    version: THEME_FILE_VERSION,
    id: canonicalTheme.id,
    name: canonicalTheme.name,
    appearance: canonicalTheme.appearance,
    colors: base,
    ...(Object.keys(other).length > 0
      ? {
          variants: canonicalTheme.appearance === "light" ? { dark: other } : { light: other },
        }
      : {}),
    ...(canonicalTheme.collection ? { collection: canonicalTheme.collection } : {}),
  };
  return `${JSON.stringify(file, null, 2)}\n`;
}

// ---------------------------------------------------------------------------
// Halves: a different theme per resolved appearance.
// ---------------------------------------------------------------------------
